import api from './api';

export interface DashboardStats {
    revenue: {
        value: number;
        previous: number;
        growth: number;
    };
    orders: {
        value: number;
        previous: number;
        growth: number;
    };
    averageOrderValue: number;
    activeCarts: number;
    pendingOrders: number;
    lowStock: number;
}

export interface SalesDataPoint {
    _id: string; // Date "YYYY-MM-DD"
    sales: number;
    orders: number;
}

export interface ProductPerformance {
    name: string;
    revenue: number;
    quantity: number;
    stock: number;
    price: number;
}

export const reportService = {
    getStats: async () => {
        const response = await api.get<{ status: string, data: DashboardStats }>('/admin/reports/stats');
        return response.data; // Helper to return the whole response or just data? 
        // Backend returns { status: 'success', data: stats }. 
        // Frontend likely expects just the data if using pre-existing patterns, 
        // but let's stick to returning response.data which is { status, data } usually, 
        // OR better yet, extract the data here to simplify components.
        // However, standard axios response.data is the body.
        // So response.data.data is the actual stats object.
        // Let's modify this to return exactly what components need if possible, or stay raw.
        // Previous fetchOrderStats returned `response.data`.
        // Old /orders returned: { totalOrders, ... } directly.
        // New /stats returns: { status: 'success', data: { ... } }
        // So I should return response.data.data to match expected shape if I want straightforward migration.
    },

    // Simplification: Let's expose the raw axios promise or normalized data.
    // Given usage in Reports.tsx: const [orderStats, setOrderStats] = useState<any>(null);
    // It sets whatever comes back.
    // I will return the data object directly.

    getDashboardStats: async (): Promise<DashboardStats> => {
        const response = await api.get<{ status: string, data: DashboardStats }>('/admin/reports/stats');
        return response.data.data;
    },

    getSalesChart: async (): Promise<SalesDataPoint[]> => {
        const response = await api.get<{ status: string, data: SalesDataPoint[] }>('/admin/reports/analytics/sales');
        return response.data.data;
    },

    getTopProducts: async (): Promise<ProductPerformance[]> => {
        const response = await api.get<{ status: string, data: ProductPerformance[] }>('/admin/reports/analytics/products');
        return response.data.data;
    },

    getProductionReport: async (params: { date?: string, city?: string, status?: string }) => {
        const response = await api.get('/admin/reports/production', { params });
        return response.data;
    },

    getInventoryAlerts: async () => {
        const response = await api.get<{ status: string, data: { critical: any[], low: any[] } }>('/admin/reports/inventory-alerts');
        return response.data.data;
    },

    getPredictiveInventory: async () => {
        const response = await api.get<{ status: string, data: any[] }>('/admin/reports/analytics/inventory-prediction');
        return response.data.data;
    },

    getUnitEconomics: async () => {
        const response = await api.get<{ status: string, data: any }>('/admin/reports/analytics/unit-economics');
        return response.data.data;
    },

    getRetentionStats: async () => {
        const response = await api.get<{ status: string, data: any }>('/admin/reports/analytics/retention');
        return response.data.data;
    }
};
